import { GrantCategory, ConnectedApp } from '../types/grant';

export interface FilterConfig {
  id: string;
  label: string;
  type: 'select' | 'multiselect' | 'range' | 'boolean';
  options?: { value: string; label: string }[];
}

export interface CategoryDefinition {
  id: GrantCategory;
  name: string;
  description: string;
  icon: string;
  themeColor: string;
  bgGradient: string;
  relevantFilters: string[];
  highlightFields: string[];
}

export const categories: CategoryDefinition[] = [
  {
    id: 'disability',
    name: 'Disability Support',
    description: 'Funding for assistive devices, therapy, and accessibility modifications.',
    icon: '♿',
    themeColor: 'bg-indigo-600',
    bgGradient: 'from-indigo-500 to-purple-600',
    relevantFilters: ['province', 'disabilityType', 'ageRange', 'financialNeed'],
    highlightFields: ['disabilityTypes', 'amount'],
  },
  {
    id: 'small-business',
    name: 'Small Business & Startups',
    description: 'Grants, wage subsidies, and tax credits for entrepreneurs.',
    icon: '💼',
    themeColor: 'bg-emerald-600',
    bgGradient: 'from-emerald-500 to-teal-600',
    relevantFilters: ['province', 'businessStage', 'industry', 'fundingType'],
    highlightFields: ['matchingFundsRequired', 'businessStages'],
  },
  {
    id: 'indigenous',
    name: 'Indigenous Communities',
    description: 'Support for First Nations, Inuit, and Métis individuals and organizations.',
    icon: '🪶',
    themeColor: 'bg-orange-600',
    bgGradient: 'from-orange-500 to-amber-600',
    relevantFilters: ['province', 'indigenousIdentity', 'communityBased'],
    highlightFields: ['indigenousIdentity', 'communityBased'],
  },
  {
    id: 'housing',
    name: 'Home & Housing',
    description: 'Renovation rebates, energy upgrades, and accessibility modifications.',
    icon: '🏠',
    themeColor: 'bg-blue-500',
    bgGradient: 'from-blue-500 to-cyan-600',
    relevantFilters: ['province', 'housingType', 'renovationType'],
    highlightFields: ['housingType', 'amount'],
  },
  {
    id: 'education',
    name: 'Education & Training',
    description: 'Scholarships, bursaries, and skills development funding.',
    icon: '🎓',
    themeColor: 'bg-violet-600',
    bgGradient: 'from-violet-500 to-purple-600',
    relevantFilters: ['province', 'educationLevel', 'fieldOfStudy'],
    highlightFields: ['amount', 'deadline'],
  },
  {
    id: 'arts',
    name: 'Arts & Culture',
    description: 'Funding for artists, cultural organizations, and creative projects.',
    icon: '🎨',
    themeColor: 'bg-pink-600',
    bgGradient: 'from-pink-500 to-rose-600',
    relevantFilters: ['province', 'artForm', 'projectType'],
    highlightFields: ['amount', 'deadline'],
  },
];

// Connected apps - your existing grant search tools
export const connectedApps: ConnectedApp[] = [
  {
    id: 'canability-grants',
    name: 'CanAbility Grants',
    description: 'AI-powered grant advisor for disability support with accessible, easy-to-read guidance.',
    category: 'disability',
    url: 'https://canabilitygrants.vercel.app',
    icon: '♿',
    themeColor: 'bg-indigo-600',
    isExternal: false,
    features: [
      'Grade 3 reading level support',
      'Split-screen conversation view',
      'Yes/No question interface',
      'Document generation assistance',
    ],
  },
  {
    id: 'small-business-grants',
    name: 'CanGrant Pro',
    description: 'Comprehensive small business grant finder with business profile matching.',
    category: 'small-business',
    url: 'https://cangrantpro.vercel.app',
    icon: '💼',
    themeColor: 'bg-emerald-600',
    isExternal: false,
    features: [
      'Business profile matching',
      'Grant tracking & saving',
      'Industry-specific search',
      'Application progress tracking',
    ],
  },
  {
    id: 'cangrant-search',
    name: 'CanGrant Search',
    description: 'General Canadian grant search platform for various funding opportunities.',
    category: 'small-business',
    url: 'https://cangrant-search.vercel.app',
    icon: '🔍',
    themeColor: 'bg-blue-600',
    isExternal: false,
    features: [
      'Broad grant database',
      'Province filtering',
      'AI-powered recommendations',
      'Source verification',
    ],
  },
];

export const PROVINCES = [
  { value: 'all', label: 'All Provinces' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];
